import { StateCreator } from 'zustand/vanilla'

import {
	ActiveFilterChoose,
	ActiveFilterRange,
	ActiveFilters,
	ActiveFiltersMap,
	Filter,
	FilterType
} from '@wwt/entities/filters'

import { excludeEmptyFilters } from './utils/excludeEmptyFilters'
import { generageActiveFiltersMapFromFilters } from './utils/generageActiveFiltersFromFilters.ts'

type State = {
	/**
	 * Map of active filters for easy access to any
	 */
	activeFilters: ActiveFiltersMap

	/**
	 * Save information when the form is dirtied
	 */
	dirty: boolean
}

type Actions = {
	/**
	 * Make form touched
	 */
	setDirty: (dirty: boolean) => void

	/**
	 * Update option info
	 */
	updateOption: (newOption: ActiveFilterChoose) => void

	/**
	 * Update range info
	 */
	updateRange: (
		newRange: Omit<ActiveFilterRange, 'defaultMin' | 'defaultMax'>
	) => void

	/**
	 * Initialize store with values from ActiveFilter array
	 *
	 * We can get this array from URLSearchParams or API(future)
	 */
	setFromActiveFilters: (filtersData: ActiveFilters) => void

	/**
	 * Initialize store from FiltersMap
	 *
	 * We can use on create second instance of this store
	 * in BigFilter for example
	 * And we want to write the value from this store into it
	 */
	setFromFiltersMap: (filtersMap: ActiveFiltersMap) => void

	/**
	 * Rewrite all filters map in store with new based on Filters from API
	 */
	reset: (filtersData: Filter[]) => void

	/**
	 *
	 */
	addNewItemsFromFilters: (filtersData: Filter[]) => void

	/**
	 * Get store value as array. Used for send to API
	 */
	getResultValue: () => ActiveFilters

	/**
	 * Get store value as map. Used for initialize new store's/other
	 */
	getRawValue: () => ActiveFiltersMap

	/**
	 * Clear store
	 */
	clearStore: () => void
}

/**
 * Default filters store
 *
 * Used for save ActiveFilter's as map
 */
export type FilterStore = State & Actions

export const filterStoreBody: StateCreator<FilterStore> = (set, get) => ({
	activeFilters: {},
	dirty: false,
	setDirty: dirty => set({ dirty }),
	getRawValue: () => {
		return get().activeFilters
	},
	getResultValue: () => {
		const storeFilters = Object.values(get().activeFilters)

		return excludeEmptyFilters(storeFilters)
	},
	updateOption: ({ id, type, optionIDs }) =>
		set(state => {
			if (type !== FilterType.AND && type !== FilterType.OR) {
				return state
			}

			const element = state.activeFilters[id]

			if (
				element &&
				element.id === id &&
				element.type === type &&
				// TODO: Rewrite to normal check arrays to equal function
				element.optionIDs.toSorted().toString() ===
					optionIDs.toSorted().toString()
			) {
				return state
			}

			return {
				activeFilters: {
					...state.activeFilters,
					[id]: {
						id,
						type,
						optionIDs
					}
				},
				dirty: true
			}
		}),
	updateRange: ({ id, type, rangeLower, rangeUpper }) =>
		set(state => {
			if (type !== FilterType.RANGE && type !== FilterType.PRICE_RANGE) {
				return state
			}

			const element = state.activeFilters[id]

			if (element && element.type !== type) {
				return state
			}

			if (
				element.id === id &&
				element.rangeLower === rangeLower &&
				element.rangeUpper === rangeUpper
			) {
				return state
			}

			return {
				activeFilters: {
					...state.activeFilters,
					[id]: {
						...element,
						id,
						type,
						rangeLower,
						rangeUpper
					}
				},
				dirty: true
			}
		}),
	setFromFiltersMap: filtersMap =>
		set(state => {
			return {
				...state,
				activeFilters: filtersMap
			}
		}),
	setFromActiveFilters: filtersData =>
		set(state => {
			const newFilters = filtersData.reduce(
				(acc, filterItem) => ({ ...acc, [filterItem.id]: filterItem }),
				{}
			)

			return {
				...state,
				activeFilters: newFilters
			}
		}),

	reset: filtersData =>
		set(state => ({
			...state,
			activeFilters: generageActiveFiltersMapFromFilters(filtersData),
			dirty: true
		})),

	addNewItemsFromFilters: filtersData =>
		set(state => ({
			...state,
			activeFilters: generageActiveFiltersMapFromFilters(
				filtersData,
				state.activeFilters
			)
		})),

	clearStore: () =>
		set(state => ({
			...state,
			activeFilters: {},
			dirty: false
		}))
})
